import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { NgxSpinnerService } from 'ngx-spinner';
import { isFalsy } from 'utility-types';
import { TiposAjusteContrato } from '@models/base/identificadores.model';
import {
  ImportarIndicesBcraRequest,
  ImportarIndicesBcraResponse,
  IndiceImportadoDto
} from '@models/indice-ajuste-contrato/importarIndicesBcra.model';
import { IndiceAjusteContratoService } from '@services/indice-ajuste-contrato/indiceAjusteContrato.service';
import { SnackBarService } from '@services/snackBar/snack-bar-service.service';

/**
 * Importación de índices desde la API del BCRA.
 *
 * El BCRA acepta rangos, así que traer un año es una sola llamada. Los valores
 * que ya están cargados se saltean y se informan: nunca se pisa algo cargado a
 * mano, y por eso se puede reimportar el mismo rango sin consecuencias.
 *
 * Sólo ICL y dólar oficial: el IPC lo publica el INDEC y se carga a mano.
 */
@Component({
  selector: 'app-dialog-importar-bcra',
  templateUrl: './dialog-importar-bcra.component.html',
  styleUrls: ['./dialog-importar-bcra.component.css']
})
export class DialogImportarBcraComponent implements OnInit {

  form: FormGroup;
  importando: boolean = false;
  resultado: ImportarIndicesBcraResponse | null = null;

  dataSource = new MatTableDataSource<IndiceImportadoDto>();
  columnas: string[] = ['fecha', 'valor', 'estado'];

  /** Los únicos que publica el BCRA */
  tiposDisponibles = [
    { id: TiposAjusteContrato.ICL, descripcion: 'Índice Contrato Locación (ICL)' },
    { id: TiposAjusteContrato.USD, descripcion: 'Dólar oficial (USD)' }
  ];

  get f() { return this.form.controls; }

  constructor(
    public dialogRef: MatDialogRef<DialogImportarBcraComponent>,
    private fb: FormBuilder,
    private indiceService: IndiceAjusteContratoService,
    private snackBar: SnackBarService,
    private spinner: NgxSpinnerService
  ) {
    // Por defecto, el último mes: es el caso habitual de ponerse al día.
    const hasta = new Date();
    const desde = new Date();
    desde.setMonth(desde.getMonth() - 1);

    this.form = this.fb.group({
      idTipoAjusteContrato: [TiposAjusteContrato.ICL, Validators.required],
      fechaDesde: [desde, Validators.required],
      fechaHasta: [hasta, Validators.required]
    });
  }

  ngOnInit(): void { }

  importar() {
    if (this.form.invalid) {
      this.snackBar.showError("Completá el tipo de índice y el rango de fechas.", "Atencion");
      return;
    }

    const desde: Date = this.f.fechaDesde.value;
    const hasta: Date = this.f.fechaHasta.value;

    if (desde > hasta) {
      this.snackBar.showError("La fecha desde es posterior a la fecha hasta.", "Atencion");
      return;
    }

    const request = new ImportarIndicesBcraRequest();
    request.idTipoAjusteContrato = this.f.idTipoAjusteContrato.value;
    request.fechaDesde = this.aTexto(desde);
    request.fechaHasta = this.aTexto(hasta);

    this.importando = true;
    this.resultado = null;
    this.spinner.show("spImportarBcra");

    this.indiceService.importarDesdeBcra(request).subscribe(
      data => {
        this.importando = false;
        this.spinner.hide("spImportarBcra");

        this.resultado = isFalsy(data) ? new ImportarIndicesBcraResponse() : data;
        this.dataSource.data = this.resultado.detalle ?? [];

        if (this.resultado.importados > 0)
          this.snackBar.showSuccess(this.resultado.mensaje, "Exito");
        else
          this.snackBar.showInfo(this.resultado.mensaje, "Información");
      },
      error => {
        this.importando = false;
        this.spinner.hide("spImportarBcra");
        this.snackBar.showError(error, "Error");
      }
    );
  }

  /** El back espera yyyy-MM-dd; mandar un Date deja que la zona horaria corra un día. */
  private aTexto(fecha: Date): string {
    const anio = fecha.getFullYear();
    const mes = (fecha.getMonth() + 1).toString().padStart(2, '0');
    const dia = fecha.getDate().toString().padStart(2, '0');

    return `${anio}-${mes}-${dia}`;
  }

  hayResultado(): boolean {
    return !isFalsy(this.resultado);
  }

  /** Se cierra devolviendo si hubo altas, para que la pantalla recargue sólo si hace falta. */
  cerrar() {
    this.dialogRef.close(this.resultado?.importados ?? 0);
  }

  trackByItems(index: number, item: any): any { return index; }
}
